import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import VideoStage, { useFullscreen } from '../AnnotationStudio/VideoStage';
import TransportBar from '../AnnotationStudio/TransportBar';
import { annotationsActiveAt } from '../AnnotationStudio/annotationModel';
import { useMediaPlayer } from '../Timeline/useMediaPlayer';
import { getAnnotationCategory } from '../../utils/annotationCategories';
import { formatTimecode } from '../../utils/timecode';
import type { ReviewAnnotation } from './types';

interface ReviewStageProps {
  videoUrl: string | null;
  fps: number;
  /** Notes drawn on the picture and the scrub bar (the original footage's). */
  notes: ReviewAnnotation[];
  /** Shown on the black stage when there is no video yet. */
  placeholder: React.ReactNode;
  /** Start playing as soon as the video is on the stage. */
  autoPlay: boolean;
  onAutoPlayed: () => void;
  /** Go to this moment (and stay paused) once the video's metadata is in. */
  seekRequest: { time: number; nonce: number } | null;
  onSeekHandled: (nonce: number) => void;
}

const isTypingTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return true;
  return Boolean(el.closest('[role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]'));
};

/**
 * One source on the stage: the studio's picture and transport with their own
 * player state. The review player gives each source a fresh stage (by key),
 * because swapping `src` on a playing <video> pauses it without a `pause`
 * event and would leave the transport showing the wrong state.
 */
const ReviewStage: React.FC<ReviewStageProps> = ({
  videoUrl,
  fps,
  notes,
  placeholder,
  autoPlay,
  onAutoPlayed,
  seekRequest,
  onSeekHandled,
}) => {
  const player = useMediaPlayer({ fps });
  const fullscreen = useFullscreen<HTMLDivElement>();
  const { element, play, pause, seek } = player;

  useEffect(() => {
    if (!autoPlay || !videoUrl || !element) return;
    play();
    onAutoPlayed();
  }, [autoPlay, videoUrl, element, play, onAutoPlayed]);

  const handledSeek = useRef<number | null>(null);
  useEffect(() => {
    if (!seekRequest || !videoUrl || !element || handledSeek.current === seekRequest.nonce) return undefined;
    const apply = () => {
      handledSeek.current = seekRequest.nonce;
      onSeekHandled(seekRequest.nonce);
      pause();
      seek(seekRequest.time);
    };
    if (element.readyState >= 1) {
      apply();
      return undefined;
    }
    element.addEventListener('loadedmetadata', apply, { once: true });
    return () => element.removeEventListener('loadedmetadata', apply);
  }, [seekRequest, videoUrl, element, pause, seek, onSeekHandled]);

  // The studio's keyboard shortcuts, while a video is on the stage.
  const shortcutRef = useRef<(event: KeyboardEvent) => void>(() => undefined);
  shortcutRef.current = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    if (!videoUrl || !player.element || isTypingTarget(event.target)) return;
    const target = event.target as HTMLElement | null;
    const nativeControl = Boolean(target && ['BUTTON', 'A'].includes(target.tagName));
    switch (event.code) {
      case 'Space':
        if (nativeControl) return;
        event.preventDefault();
        player.toggle();
        break;
      case 'KeyK': event.preventDefault(); player.pause(); break;
      case 'KeyJ': event.preventDefault(); player.shuttleReverse(); break;
      case 'KeyL': event.preventDefault(); player.shuttleForward(); break;
      case 'ArrowLeft': event.preventDefault(); player.skip(event.shiftKey ? -10 : -5); break;
      case 'ArrowRight': event.preventDefault(); player.skip(event.shiftKey ? 10 : 5); break;
      case 'Comma': event.preventDefault(); player.stepFrames(-1); break;
      case 'Period': event.preventDefault(); player.stepFrames(1); break;
      case 'KeyM': event.preventDefault(); player.toggleMute(); break;
      case 'KeyF': event.preventDefault(); fullscreen.toggle(); break;
      default: break;
    }
  };
  useEffect(() => {
    const listener = (event: KeyboardEvent) => shortcutRef.current(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  const safeNotes = useMemo(() => notes.filter((note) => note && Number.isFinite(Number(note.time))), [notes]);
  const stageNotes = useMemo(
    () =>
      annotationsActiveAt(safeNotes, player.currentTime, 2).map((note) => {
        const category = getAnnotationCategory(note.category, note.color);
        return {
          id: note.id,
          label: `${note.category ? `${category.label} · ` : ''}${formatTimecode(note.time, fps)}`,
          text: note.text,
          color: category.color,
        };
      }),
    [safeNotes, player.currentTime, fps]
  );
  const markers = useMemo(
    () =>
      safeNotes.map((note) => ({
        time: note.time,
        endTime: note.endTime,
        color: getAnnotationCategory(note.category, note.color).color,
      })),
    [safeNotes]
  );

  return (
    <Box
      ref={fullscreen.ref}
      sx={{
        bgcolor: 'common.black',
        display: 'flex',
        flexDirection: 'column',
        ...(fullscreen.isFullscreen ? { height: '100vh' } : {}),
      }}
    >
      <VideoStage
        videoUrl={videoUrl || undefined}
        player={player}
        testId="review-player-video"
        notes={stageNotes}
        fullscreen={fullscreen.isFullscreen}
        maxHeight="min(60vh, 680px)"
        placeholder={placeholder}
      />
      <TransportBar
        player={player}
        fps={fps}
        disabled={!videoUrl}
        markers={markers}
        isFullscreen={fullscreen.isFullscreen}
        onToggleFullscreen={fullscreen.toggle}
      />
    </Box>
  );
};

export default ReviewStage;
